var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from "react";
let productsCache = {};
/**
 * Hard ceiling on units per line, applied only to products that carry a minimum quantity.
 * Ordinary products keep their previous unbounded behaviour.
 */
const QUANTITY_CEILING = 10;
function emitCartLinesUpdate(action, lines = []) {
    document.dispatchEvent(new CustomEvent('shopify:cart:lines-update', {
        bubbles: true,
        detail: { source: 'theme', action, lines },
    }));
}
export const useCartManager = () => {
    const [items, setItems] = useState([]);
    const [cartOpen, setCartOpen] = useState(false);
    const [performingAction, setPerformingAction] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [categories, setCategories] = useState(globalThis.cartDrawerCategories);
    const [products, setProducts] = useState(globalThis.cartDrawerCollection);
    const [alternateVersion, setAlternateVersion] = useState(globalThis.cartDrawerSeeBVersion);
    const [emptyStateTitle, setEmptyStateTitle] = useState(globalThis.cartDrawerEmptyStateTitle);
    const [emptyStateSubtitle, setEmptyStateSubtitle] = useState(globalThis.cartDrawerEmptyStateSubtitle);
    const [emptyStateCTAText, setEmptyStateCTAText] = useState(globalThis.cartDrawerEmptyStateCTAText);
    const [emptyStateCTALink, setEmptyStateCTALink] = useState(globalThis.cartDrawerEmptyStateCTALink);
    const [emptyStateCTASubtext, setEmptyStateCTASubtext] = useState(globalThis.cartDrawerEmptyStateCTASubtext);
    const [subscriptionInfoDetails, setSubscriptionInfoDetails] = useState(globalThis.cartDrawerSubscriptionInfoDetails);
    const [subscriptionDisclosure, setSubscriptionDisclosure] = useState(globalThis.cartDrawerSubscriptionDisclosure);
    const [termsURL, setTermsURL] = useState(globalThis.cartDrawerTermsURL);
    const [totalDiscount, setTotalDiscount] = useState(0);
    /**
     * Data for the drawer's empty state.
     *
     * Each one corresponds to all the experimental variations being tested.
     */
    let emptyStateCategories;
    let emptyStateProducts;
    let seeBVersion;
    /**
     * Minimum purchase quantity per variant, keyed by variant id.
     *
     * /cart.js and /products/{handle}.js do not return metafields, so the step cannot be read
     * here directly -- templates/cart.qty-step.liquid renders it for us. A failure is
     * non-fatal: every line falls back to a minimum of 1, which is the pre-existing behaviour.
     */
    const getQuantityMinsRequest = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield fetch('/cart?view=qty-step', {
                method: 'GET',
                mode: 'same-origin',
                cache: 'no-cache',
                credentials: 'same-origin',
            });
            if (!response.ok)
                return {};
            return yield response.json();
        }
        catch (error) {
            console.log("CartDrawer unable to load minimum quantities.");
            return {};
        }
    });
    const getCart = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        try {
            setPerformingAction(true);
            const response = yield fetch('/cart.js', {
                method: 'GET',
                mode: 'same-origin',
                cache: 'no-cache',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const cart = yield response.json();
            const quantityMins = yield getQuantityMinsRequest();
            for (let cartItem of cart.items) {
                let productInfo = productsCache[cartItem.id];
                if (!productInfo) {
                    productInfo = yield fetch(`/products/${cartItem.handle}.js`, {
                        method: 'GET',
                        mode: 'same-origin',
                        cache: 'no-cache',
                        credentials: 'same-origin',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    });
                    productInfo = yield productInfo.json();
                    productsCache[cartItem.id] = productInfo;
                }
                cartItem.product = productInfo;
                cartItem.isGiftAvailable = (productInfo.variants || []).some((v) => v.id === cartItem.variant_id && v.available);
                cartItem.isSubscribable = !!productInfo.selling_plan_groups[0];
                cartItem.quantityMin = ((_a = quantityMins[cartItem.variant_id]) === null || _a === void 0 ? void 0 : _a.quantity_min) || 1;
            }
            setItems(cart.items);
            setTotalDiscount(cart.total_discount / 100);
            updateCartCount(cart.items);
            window.cartManager.items = cart.items;
            setPerformingAction(false);
        }
        catch (error) {
            console.log("CartDrawer unable to load.");
        }
        finally {
            setPerformingAction(false);
            setIsLoading(false);
        }
    });
    const updateCartCount = (items) => {
        const cartElements = document.querySelectorAll('.cartCount');
        const qty = items.length > 0 ? items.reduce((previous, item) => previous + item.quantity, 0) : 0;
        if (cartElements.length > 0) {
            for (const cartElement of cartElements) {
                cartElement.innerHTML = qty.toString();
            }
        }
    };
    const addItem = (items) => __awaiter(void 0, void 0, void 0, function* () {
        var _b;
        setPerformingAction(true);
        const response = yield fetch('/cart/add.js', {
            method: 'POST',
            mode: 'same-origin',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(items),
        });
        if (!response.ok) {
            // Shopify puts a shopper-facing sentence in the body here -- cart validation
            // functions, inventory limits and invalid variants all surface this way. Before
            // this check the status was ignored, so a rejected add still opened the drawer on
            // an unchanged cart and read as "nothing happened".
            let message = 'Sorry, we could not add that to your cart.';
            try {
                const body = yield response.json();
                message = body.description || body.message || message;
            }
            catch (error) {
                // non-JSON error body; keep the generic message
            }
            document.dispatchEvent(new CustomEvent('cart:add-error', {
                bubbles: true,
                detail: { message, status: response.status, items },
            }));
            setPerformingAction(false);
            return; // deliberately no getCart()/openCart() -- nothing was added
        }
        yield getCart();
        emitCartLinesUpdate('add', (_b = items.items) !== null && _b !== void 0 ? _b : []);
        openCart();
        setPerformingAction(false);
    });
    const removeItem = (itemKey) => __awaiter(void 0, void 0, void 0, function* () {
        setPerformingAction(true);
        const response = yield fetch('/cart/update.js', {
            method: 'POST',
            mode: 'same-origin',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                updates: {
                    [itemKey]: 0,
                }
            })
        });
        yield getCart();
        emitCartLinesUpdate('remove', [{ id: itemKey, quantity: 0 }]);
        setPerformingAction(false);
    });
    const updateItem = (data) => __awaiter(void 0, void 0, void 0, function* () {
        yield fetch('/cart/update.js', {
            method: 'POST',
            mode: 'same-origin',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        yield getCart();
        emitCartLinesUpdate('update', Object.entries(data.updates).map(([id, quantity]) => ({ id, quantity })));
    });
    const changeItem = (item, optionIndex, cartItemIndex) => __awaiter(void 0, void 0, void 0, function* () {
        setPerformingAction(true);
        //get product and its selling plan
        const response = yield fetch(`/products/${item.handle}.js`, {
            method: 'GET',
            mode: 'same-origin',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        const product = yield response.json();
        const data = {
            line: cartItemIndex,
            quantity: item.quantity,
            selling_plan: product.selling_plan_groups[0].selling_plans[optionIndex].id,
        };
        yield fetch('/cart/change.js', {
            method: 'POST',
            mode: 'same-origin',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        yield getCart();
        emitCartLinesUpdate('update', []);
        setPerformingAction(false);
    });
    const removeSubscription = (item, cartItemIndex) => __awaiter(void 0, void 0, void 0, function* () {
        setPerformingAction(true);
        const data = {
            line: cartItemIndex,
            quantity: item.quantity,
            selling_plan: null,
        };
        yield fetch('/cart/change.js', {
            method: 'POST',
            mode: 'same-origin',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        yield getCart();
        emitCartLinesUpdate('update', []);
        setPerformingAction(false);
    });
    const increaseQty = (item) => __awaiter(void 0, void 0, void 0, function* () {
        const min = item.quantityMin || 1;
        const next = item.quantity + 1;
        // The ceiling only applies to floored products; everything else is unbounded as before.
        if (min > 1 && next > QUANTITY_CEILING)
            return;
        setPerformingAction(true);
        const data = {
            updates: {
                [item.key]: next,
            }
        };
        yield updateItem(data);
        setPerformingAction(false);
    });
    const decreaseQty = (item) => __awaiter(void 0, void 0, void 0, function* () {
        const min = item.quantityMin || 1;
        // Below the minimum there is no valid quantity, so the line goes away. With a minimum of
        // 2 that means minus at 2 removes both units; with a minimum of 1 it is the old 1 -> 0.
        const next = item.quantity - 1;
        setPerformingAction(true);
        const data = {
            updates: {
                [item.key]: next < min ? 0 : next,
            }
        };
        yield updateItem(data);
        setPerformingAction(false);
    });
    const clearCart = () => __awaiter(void 0, void 0, void 0, function* () {
        setPerformingAction(true);
        yield fetch('/cart/clear.js', {
            method: 'POST',
            mode: 'same-origin',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(""),
        });
        yield getCart();
        emitCartLinesUpdate('remove', []);
        setPerformingAction(false);
    });
    const openCart = () => {
        setCartOpen(true);
        window.cartManager.cartOpen = true;
    };
    const closeCart = () => {
        setCartOpen(false);
        window.cartManager.cartOpen = false;
    };
    // exposing cart manager to window
    window.cartManager = {
        categories,
        products,
        alternateVersion,
        setCategories,
        setProducts,
        setAlternateVersion,
        items,
        totalDiscount,
        cartOpen,
        performingAction,
        isLoading,
        addItem,
        removeItem,
        updateItem,
        removeSubscription,
        changeItem,
        clearCart,
        openCart,
        closeCart,
        increaseQty,
        decreaseQty,
        getCart,
        emptyStateTitle,
        setEmptyStateTitle,
        emptyStateSubtitle,
        setEmptyStateSubtitle,
        emptyStateCTAText,
        setEmptyStateCTAText,
        emptyStateCTALink,
        setEmptyStateCTALink,
        emptyStateCTASubtext,
        setEmptyStateCTASubtext,
        subscriptionInfoDetails,
        setSubscriptionInfoDetails,
        subscriptionDisclosure,
        setSubscriptionDisclosure,
        termsURL,
        setTermsURL
    };
    return { items, totalDiscount, cartOpen, performingAction, isLoading, getCart, addItem, removeItem, updateItem, removeSubscription, changeItem, clearCart, openCart, closeCart, increaseQty, decreaseQty, categories, setCategories, products, setProducts, alternateVersion, setAlternateVersion, emptyStateTitle, setEmptyStateTitle, emptyStateSubtitle, setEmptyStateSubtitle, emptyStateCTAText, setEmptyStateCTAText, emptyStateCTALink, setEmptyStateCTALink, emptyStateCTASubtext, setEmptyStateCTASubtext, subscriptionInfoDetails, setSubscriptionInfoDetails, subscriptionDisclosure, setSubscriptionDisclosure, termsURL, setTermsURL };
};
